import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CompanyInterface } from '@app/interface/company';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CompanyApi } from '@app/services/company-api';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { TypeOptions } from '@app/interface/type-options';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogData } from '@app/new-company/new-company.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { SubscriptionInterface } from '@app/interface/subscription';
import { Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Organizations } from '@app/interface/organizations';

export interface ManageSubscriptionDialogData {
  subscription: SubscriptionInterface;
  organizationId: number;
  organization: Organizations;
}

@Component({
  selector: 'izy-manage-subscription-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSnackBarModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './manage-subscription-dialog.component.html',
  styleUrl: './manage-subscription-dialog.component.scss',
})
export class ManageSubscriptionDialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  subscriptionForm: FormGroup;
  isLoading = false;

  // mailAddress: string = 'info@izysrl.it';
  helpEmail: string = 'info@forfettari-online.it';
  helpNumber: string = '800 950 206';

  constructor(
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<ManageSubscriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageSubscriptionDialogData,
  ) {
    this.subscriptionForm = new FormGroup({
      auto_renew: new FormControl(false),
      help_email: new FormControl(''),
      help_number: new FormControl(''),
    });
  }

  ngOnInit(): void {
    // Initialize form with current subscription data
    if (this.data.subscription) {
      this.subscriptionForm.patchValue({
        auto_renew: this.data.subscription.auto_renew,
      });
    }

    if (this.data.organization) {
      this.helpEmail = this.data.organization.help_email;
      this.helpNumber = this.data.organization.help_number;

      this.subscriptionForm.patchValue({
        help_email: this.data.organization.help_email,
        help_number: this.data.organization.help_number,
      });
    }
  }

  copyToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpEmail)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Indirizzo mail copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  copyNumberToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpNumber)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Numero copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  openContractAttachment(attachmentUrl: string): void {
    if (attachmentUrl) {
      // Open the attachment in a new tab
      window.open(attachmentUrl, '_blank');
    }
  }

  confirm(): void {
    if (this.subscriptionForm.valid) {
      this.dialogRef.close(this.subscriptionForm.value);
    }
  }

  onToggleChange(event: any): void {
    console.log('Auto-renew changed to:', event.checked);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}

export interface HelpCenterDialogComponentData {
  subscription: SubscriptionInterface;
  organizationId: number;
  organization: Organizations;
}

@Component({
  selector: 'izy-manage-subscription-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSnackBarModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './help-center-dialog.component.html',
  styleUrl: './manage-subscription-dialog.component.scss',
})
export class HelpCenterDialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  subscriptionForm: FormGroup;
  isLoading = false;

  // mailAddress: string = 'info@izysrl.it';
  helpEmail: string = 'info@forfettari-online.it';
  helpNumber: string = '800 950 206';

  constructor(
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<HelpCenterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HelpCenterDialogComponentData,
  ) {
    this.subscriptionForm = new FormGroup({
      auto_renew: new FormControl(false),
      help_email: new FormControl(''),
      help_number: new FormControl(''),
    });
  }

  ngOnInit(): void {
    // Initialize form with current subscription data
    if (this.data.subscription) {
      this.subscriptionForm.patchValue({
        auto_renew: this.data.subscription.auto_renew,
      });
    }

    if (this.data.organization) {
      this.helpEmail = this.data.organization.help_email;
      this.helpNumber = this.data.organization.help_number;

      this.subscriptionForm.patchValue({
        help_email: this.data.organization.help_email,
        help_number: this.data.organization.help_number,
      });
    }
  }

  copyToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpEmail)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Indirizzo mail copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  copyNumberToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpNumber)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Numero copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  confirm(): void {
    if (this.subscriptionForm.valid) {
      this.dialogRef.close(this.subscriptionForm.value);
    }
  }

  onToggleChange(event: any): void {
    console.log('Auto-renew changed to:', event.checked);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}

// FIXME: still NOT used
@Component({
  selector: 'izy-manage-subscription-ticket-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSnackBarModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './manage-subscription-ticket-dialog.component.html',
  styleUrl: './manage-subscription-dialog.component.scss',
})
export class ManageSubscriptionTicketDialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  subscriptionForm: FormGroup;
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<ManageSubscriptionTicketDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageSubscriptionDialogData,
  ) {
    this.subscriptionForm = new FormGroup({
      auto_renew: new FormControl(false),
    });
  }

  ngOnInit(): void {
    // Initialize form with current subscription data
    if (this.data.subscription) {
      this.subscriptionForm.patchValue({
        auto_renew: this.data.subscription.auto_renew,
      });
    }
  }

  // copyToClipboard(): void {
  //   navigator.clipboard
  //     .writeText(this.mailAddress)
  //     .then(() => {
  //       // You could show a small confirmation here or rely on the parent
  //       this.dialogRef.close(true); // Close with success
  //     })
  //     .catch((err) => {
  //       console.error('Failed to copy: ', err);
  //       this.dialogRef.close(false); // Close with failure
  //     });
  // }

  confirm(): void {
    if (this.subscriptionForm.valid) {
      this.dialogRef.close(this.subscriptionForm.value);
    }
  }

  onToggleChange(event: any): void {
    console.log('Auto-renew changed to:', event.checked);
  }
}

export interface DeleteAccountDialogComponentData {
  subscription: SubscriptionInterface;
  organizationId: number;
  organization: Organizations;
}

@Component({
  selector: 'delete-account-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSnackBarModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './delete-account-dialog.component.html',
  styleUrl: './manage-subscription-dialog.component.scss',
})
export class DeleteAccountDialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  subscriptionForm: FormGroup;
  isLoading = false;

  // mailAddress: string = 'info@izysrl.it';
  helpEmail: string = 'info@forfettari-online.it';
  helpNumber: string = '800 950 206';

  constructor(
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<DeleteAccountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeleteAccountDialogComponentData,
  ) {
    this.subscriptionForm = new FormGroup({
      auto_renew: new FormControl(false),
      help_email: new FormControl(''),
      help_number: new FormControl(''),
    });
  }

  ngOnInit(): void {
    // Initialize form with current subscription data
    if (this.data.subscription) {
      this.subscriptionForm.patchValue({
        auto_renew: this.data.subscription.auto_renew,
      });
    }

    if (this.data.organization) {
      this.helpEmail = this.data.organization.help_email;
      this.helpNumber = this.data.organization.help_number;

      this.subscriptionForm.patchValue({
        help_email: this.data.organization.help_email,
        help_number: this.data.organization.help_number,
      });
    }
  }

  copyToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpEmail)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Indirizzo mail copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  copyNumberToClipboard(): void {
    navigator.clipboard
      .writeText(this.helpNumber)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Numero copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  confirm(): void {
    if (this.subscriptionForm.valid) {
      this.dialogRef.close(this.subscriptionForm.value);
    }
  }

  onToggleChange(event: any): void {
    console.log('Auto-renew changed to:', event.checked);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
